import {
  subscribe,
  getState,
  addLocationLocal,
  updateLocationLocal,
  removeLocationLocal,
  setDiseaseOverride
} from '../state/store.js';
import { createLocation, updateLocation, deleteLocation } from '../services/supabase.js';

const locationsTbody = document.getElementById('locationsTbody');
const locationsCards = document.getElementById('locationsCards');
const addLocationBtn = document.getElementById('addLocationBtn');
const locationFormModal = document.getElementById('locationFormModal');
const locationForm = document.getElementById('locationForm');
const locationFormTitle = document.getElementById('locationFormTitle');
const locationFormSubmitBtn = document.getElementById('locationFormSubmitBtn');
const locationFormCancel = document.getElementById('locationFormCancel');
const locationFormDeleteBtn = document.getElementById('locationFormDeleteBtn');
const diseaseOverrideToggle = document.getElementById('diseaseOverrideToggle');
const diseaseOverrideFields = document.getElementById('diseaseOverrideFields');
const diseaseOverrideDisease = document.getElementById('diseaseOverrideDisease');
const diseaseOverrideParasite = document.getElementById('diseaseOverrideParasite');
const locationSearchInput = document.getElementById('locationSearchInput');

const CARD_LONG_PRESS_MS = 500;
const CARD_MOVE_TOLERANCE = 10;

let editingLocationId = null;
let editingLocationName = '';
let cardPressTimer = null;
let cardLongPressFired = false;
let cardPressStart = { x: 0, y: 0 };
let locationSearchTerm = '';

const escapeHTML = (value = '') =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const openLocationForm = (record = null) => {
  locationForm?.reset();
  editingLocationId = record ? record.id : null;
  editingLocationName = record ? record.location_name || '' : '';
  if (locationFormTitle) locationFormTitle.textContent = record ? 'Edit Location' : 'Add Location';
  if (locationFormSubmitBtn) locationFormSubmitBtn.textContent = record ? 'Save Changes' : 'Save';
  if (locationFormDeleteBtn) locationFormDeleteBtn.hidden = !record;
  if (record && locationForm) {
    locationForm.elements.location_name.value = record.location_name || '';
    locationForm.elements.hex_number.value = record.hex_number || '';
    locationForm.elements.disease_modifier.value = record.disease_modifier ?? 0;
    locationForm.elements.parasite_modifier.value = record.parasite_modifier ?? 0;
    locationForm.elements.notes.value = record.notes || '';
  }
  locationFormModal?.showModal();
};

const closeLocationForm = () => locationFormModal?.close();

const handleLocationSubmit = async (event) => {
  event.preventDefault();
  const formData = Object.fromEntries(new FormData(locationForm).entries());
  const locationName = String(formData.location_name || '').trim();
  if (!locationName) {
    alert('Location Name is required.');
    return;
  }
  const payload = {
    location_name: locationName,
    hex_number: formData.hex_number,
    disease_modifier: formData.disease_modifier,
    parasite_modifier: formData.parasite_modifier,
    notes: formData.notes
  };
  try {
    if (editingLocationId) {
      const updated = await updateLocation(editingLocationId, payload);
      updateLocationLocal(editingLocationId, updated);
      editingLocationId = null;
      closeLocationForm();
      alert('Location updated.');
      return;
    }
    const created = await createLocation(payload);
    addLocationLocal(created);
    closeLocationForm();
    alert('Location added.');
  } catch (error) {
    alert(`Failed to save location: ${error.message}`);
  }
};

const handleDeleteLocation = async (id, name) => {
  const confirmed = window.confirm(`Delete ${name || 'this location'}?`);
  if (!confirmed) return false;
  try {
    await deleteLocation(id);
    removeLocationLocal(id);
    return true;
  } catch (error) {
    alert(`Failed to delete location: ${error.message}`);
    return false;
  }
};

const handleModalDelete = async () => {
  if (!editingLocationId) return;
  const deleted = await handleDeleteLocation(editingLocationId, editingLocationName);
  if (deleted) closeLocationForm();
};

const findLocationRecord = (id) => {
  const { locations } = getState();
  return locations.find((loc) => loc.id === id);
};

const clearCardPressTimer = () => {
  clearTimeout(cardPressTimer);
  cardPressTimer = null;
};

const getFilteredLocations = (locations) => {
  const query = locationSearchTerm.trim().toLowerCase();
  if (!query) return locations;
  return locations.filter((loc) => (loc.location_name || '').toLowerCase().includes(query));
};

const renderLocationsList = () => {
  const { locations, diseaseOverride } = getState();
  const filtered = getFilteredLocations(locations);
  renderLocationsTable(filtered, diseaseOverride);
  renderLocationsCards(filtered, diseaseOverride);
  renderDiseaseOverrideControls(diseaseOverride);
};

const getDisplayModifiers = (loc, diseaseOverride) =>
  diseaseOverride?.enabled
    ? { disease: diseaseOverride.disease ?? 0, parasite: diseaseOverride.parasite ?? 0 }
    : { disease: loc.disease_modifier ?? 0, parasite: loc.parasite_modifier ?? 0 };

const renderDiseaseOverrideControls = (diseaseOverride) => {
  if (diseaseOverrideToggle) diseaseOverrideToggle.checked = !!diseaseOverride.enabled;
  if (diseaseOverrideFields) diseaseOverrideFields.classList.toggle('open', !!diseaseOverride.enabled);
  if (diseaseOverrideDisease && document.activeElement !== diseaseOverrideDisease) {
    diseaseOverrideDisease.value = diseaseOverride.disease ?? 0;
  }
  if (diseaseOverrideParasite && document.activeElement !== diseaseOverrideParasite) {
    diseaseOverrideParasite.value = diseaseOverride.parasite ?? 0;
  }
};

const renderLocationsTable = (locations, diseaseOverride) => {
  if (!locationsTbody) return;
  if (!locations.length) {
    const message = locationSearchTerm.trim() ? 'No locations match your search.' : 'No locations recorded yet.';
    locationsTbody.innerHTML = `<tr><td colspan="6" class="placeholder">${message}</td></tr>`;
    return;
  }
  locationsTbody.innerHTML = locations
    .map((loc) => {
      const { disease, parasite } = getDisplayModifiers(loc, diseaseOverride);
      return `<tr data-id="${loc.id}">
        <td>${escapeHTML(loc.location_name)}</td>
        <td>${escapeHTML(loc.hex_number) || '—'}</td>
        <td>${disease}</td>
        <td>${parasite}</td>
        <td>${escapeHTML(loc.notes) || '—'}</td>
        <td data-dm-only class="actions-col">
          <div class="row-actions">
            <button class="btn small danger" type="button" data-action="delete">Delete</button>
          </div>
        </td>
      </tr>`;
    })
    .join('');
};

const renderLocationsCards = (locations, diseaseOverride) => {
  if (!locationsCards) return;
  if (!locations.length) {
    const message = locationSearchTerm.trim() ? 'No locations match your search.' : 'No locations recorded yet.';
    locationsCards.innerHTML = `<p class="placeholder">${message}</p>`;
    return;
  }
  locationsCards.innerHTML = locations
    .map((loc) => {
      const hex = escapeHTML(loc.hex_number) || '—';
      const notes = escapeHTML(loc.notes);
      const { disease, parasite } = getDisplayModifiers(loc, diseaseOverride);
      return `<article class="location-row-card" data-id="${loc.id}">
        <div class="loc-main">
          <h4>${escapeHTML(loc.location_name)}</h4>
          <div class="loc-meta">Hex ${hex} · Disease ${disease}% · Parasite ${parasite}%</div>
          ${notes ? `<div class="loc-notes-hint">Tap for notes</div><div class="loc-notes">${notes}</div>` : ''}
        </div>
        <div data-dm-only class="row-actions">
          <button class="btn small outline" type="button" data-action="edit">Edit</button>
        </div>
      </article>`;
    })
    .join('');
};

export const initLocations = () => {
  addLocationBtn?.addEventListener('click', () => openLocationForm());
  locationForm?.addEventListener('submit', handleLocationSubmit);
  locationFormCancel?.addEventListener('click', closeLocationForm);

  diseaseOverrideToggle?.addEventListener('change', (event) => {
    setDiseaseOverride({ enabled: event.target.checked });
  });
  diseaseOverrideDisease?.addEventListener('input', (event) => {
    setDiseaseOverride({ disease: Number(event.target.value) || 0 });
  });
  diseaseOverrideParasite?.addEventListener('input', (event) => {
    setDiseaseOverride({ parasite: Number(event.target.value) || 0 });
  });
  locationSearchInput?.addEventListener('input', (event) => {
    locationSearchTerm = event.target.value;
    renderLocationsList();
  });
  locationFormModal?.addEventListener('close', () => {
    editingLocationId = null;
    editingLocationName = '';
  });

  locationsTbody?.addEventListener('click', (event) => {
    const row = event.target.closest('tr[data-id]');
    if (!row) return;
    const id = Number(row.dataset.id);
    const { locations } = getState();
    const record = locations.find((loc) => loc.id === id);

    const action = event.target?.dataset?.action;
    if (action === 'delete') {
      event.stopPropagation();
      handleDeleteLocation(id, record?.location_name);
      return;
    }
    if (record) openLocationForm(record);
  });

  locationFormDeleteBtn?.addEventListener('click', handleModalDelete);

  locationsCards?.addEventListener('pointerdown', (event) => {
    const card = event.target.closest('.location-row-card');
    if (!card || event.target.closest('[data-action]')) return;
    cardLongPressFired = false;
    cardPressStart = { x: event.clientX, y: event.clientY };
    cardPressTimer = setTimeout(() => {
      cardLongPressFired = true;
      const record = findLocationRecord(Number(card.dataset.id));
      if (record) openLocationForm(record);
    }, CARD_LONG_PRESS_MS);
  });

  locationsCards?.addEventListener('pointermove', (event) => {
    if (!cardPressTimer) return;
    const movedX = Math.abs(event.clientX - cardPressStart.x);
    const movedY = Math.abs(event.clientY - cardPressStart.y);
    if (movedX > CARD_MOVE_TOLERANCE || movedY > CARD_MOVE_TOLERANCE) clearCardPressTimer();
  });

  locationsCards?.addEventListener('pointerup', clearCardPressTimer);
  locationsCards?.addEventListener('pointercancel', clearCardPressTimer);

  locationsCards?.addEventListener('contextmenu', (event) => {
    if (event.target.closest('.location-row-card')) event.preventDefault();
  });

  locationsCards?.addEventListener('click', (event) => {
    clearCardPressTimer();
    if (cardLongPressFired) {
      cardLongPressFired = false;
      return;
    }
    const card = event.target.closest('[data-id]');
    if (!card) return;
    if (event.target.closest('[data-action="edit"]')) {
      const record = findLocationRecord(Number(card.dataset.id));
      if (record) openLocationForm(record);
      return;
    }
    card.classList.toggle('notes-open');
  });

  subscribe(renderLocationsList);
  renderLocationsList();
};
